import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type SxProps, type Theme } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { type PayBoostEligibilityResult } from "@src/appV2/redesign/Shift/PayBoost/getPayBoostEligibility";
import { SubsidizedPayLabel } from "@src/appV2/redesign/Shift/PayBoost/SubsidizedPayLabel";

import { HourlyPayLabel } from "./HourlyPayLabel";
import { TotalPayOrBoostLabel } from "./TotalPayOrBoostLabel";

export interface PayInfoLabelProps {
  /**
   * The formatted total pay for the shift.
   */
  totalPay: string | undefined;
  /**
   * The formatted hourly pay for the shift.
   */
  hourlyPay: string | undefined;
  /**
   * Whether the pay data is being fetched.
   */
  isLoading?: boolean;
  /**
   * Custom styles to apply to the component.
   */
  wrapperSx?: SxProps<Theme>;
  /**
   * The formatted shift boost pay for the shift.
   */
  totalWithBoostPay?: string | undefined;
  /**
   * Pay boost eligibility result for showing subsidized rate UI.
   */
  payBoostEligibility?: PayBoostEligibilityResult;
}

// TODO: Move to cbh-core as fast follow up to https://linear.app/clipboardhealth/issue/SPRTA-1362/update-shift-details-bottom-sheet
export function PayInfoLabel(props: PayInfoLabelProps) {
  const { totalPay, hourlyPay, totalWithBoostPay, isLoading, wrapperSx, payBoostEligibility } =
    props;

  const subsidizedOriginalRate =
    isDefined(payBoostEligibility) &&
    payBoostEligibility.isEligible &&
    isDefined(payBoostEligibility.originalHourlyRate)
      ? payBoostEligibility.originalHourlyRate
      : undefined;

  if (isDefined(subsidizedOriginalRate) && isDefined(hourlyPay)) {
    return (
      <Stack alignItems="flex-start" justifyContent="center" sx={wrapperSx}>
        <SubsidizedPayLabel
          originalRate={formatDollarsAsUsd(subsidizedOriginalRate)}
          subsidizedRate={hourlyPay}
          totalLabel={
            <TotalPayOrBoostLabel
              totalPay={totalPay}
              isLoading={isLoading}
              totalWithBoostPay={totalWithBoostPay}
              sx={(theme) => ({ color: theme.palette.text.tertiary })}
            />
          }
        />
      </Stack>
    );
  }

  return (
    <Stack
      spacing={1}
      direction="column"
      alignItems="flex-start"
      justifyContent="center"
      sx={wrapperSx}
    >
      <HourlyPayLabel hourlyPay={hourlyPay} isLoading={isLoading} size="small" />
      <TotalPayOrBoostLabel
        totalPay={totalPay}
        isLoading={isLoading}
        totalWithBoostPay={totalWithBoostPay}
        sx={(theme) => ({ color: theme.palette.text.tertiary })}
      />
    </Stack>
  );
}
